var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React from 'react';
import { classNames, responsiveClassNames, responsiveVariables, responsivePropDependency, } from "../../utils/helpers";
import { Divider } from "../divider";
import { Hidden } from "../hidden";
import s from './View.module.css';
const ViewItem = (props) => {
    const { columns, backgroundColor, grow, gapBefore, as: TagName = 'div', order, children, className, attributes, } = props;
    const itemClassNames = classNames(s.item, className, backgroundColor && s[`--bg-${backgroundColor}`], gapBefore === 'auto' && s['item--gap-auto'], gapBefore !== undefined && s['item--gap-before'], ...responsiveClassNames(s, 'item--grow', grow), ...responsiveClassNames(s, 'item--columns', columns));
    const itemVariables = Object.assign(Object.assign(Object.assign({}, attributes === null || attributes === void 0 ? void 0 : attributes.style), responsiveVariables('--st-_o', order)), responsiveVariables('--st-_gi', gapBefore));
    return (
    // @ts-expect-error TS throws an error due to the type complexity It still resolves the attributes correctly based on the tag
    _jsx(TagName, Object.assign({}, attributes, { style: itemVariables, className: itemClassNames, children: children })));
};
export const View = (props) => {
    const { 
    /**
     * Layout props
     */
    align, justify, wrap, gap, height, width, maxHeight, maxWidth, padding, bleed, 
    /**
     * Style props
     * */
    backgroundColor, borderColor, borderRadius, shadow, textAlign, overflow, 
    /**
     * Using any here to let TS save on type resolving, otherwise TS throws an error due to the type complexity
     * It still resolves the attributes correctly based on the tag
     */
    as: TagName = 'div', children, divided, className, attributes, } = props;
    const isFlex = !!align || !!justify || !!gap || !!props.direction;
    const direction = props.direction || (isFlex ? 'column' : undefined);
    let renderedItemIndex = 0;
    // If wrap is not defined, it can be set based on item grow and split usage
    let nowrap;
    const renderDivider = ({ className, key }) => {
        const dividerClassName = classNames(s.divider, className);
        let isDividerVertical = false;
        if (typeof direction === 'string' && direction.startsWith('row')) {
            isDividerVertical = true;
        }
        else if (direction) {
            const viewports = Object.keys(direction);
            isDividerVertical = viewports.reduce((acc, viewport) => {
                const viewportDirection = direction[viewport];
                if (!viewportDirection)
                    return acc;
                return Object.assign(Object.assign({}, acc), { [viewport]: viewportDirection.startsWith('row') });
            }, {});
        }
        return (_jsx("div", { className: dividerClassName, children: _jsx(Divider, { vertical: isDividerVertical }) }, `${key}-divider`));
    };
    const renderItem = ({ className, child, index }) => {
        var _a, _b;
        const isItem = child.type === ViewItem;
        const key = child.key || index;
        const dividerElement = !!index && divided && renderDivider({ className, key });
        let itemElement;
        if (isItem) {
            itemElement = React.cloneElement(child, {
                className: classNames(className, child.props.className),
            });
        }
        else if (className || !React.isValidElement(child)) {
            itemElement = (_jsx("div", { className: classNames(className), children: child }, key));
        }
        else {
            itemElement = child;
        }
        // Passing grow here because it's responsive and nowrap should follow it
        if (isItem && ((_a = child.props) === null || _a === void 0 ? void 0 : _a.grow))
            nowrap = child.props.grow;
        if (isItem && ((_b = child.props) === null || _b === void 0 ? void 0 : _b.gap) === 'auto')
            nowrap = true;
        return [dividerElement, itemElement];
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formattedChildren = React.Children.map(children, (child, index) => {
        if (!child)
            return null;
        // Ignore the indices of the items that rendered nothing
        const renderedIndex = renderedItemIndex;
        renderedItemIndex += 1;
        if (child.type === Hidden && typeof child.props.children !== 'function') {
            const _a = child.props, { children: hiddenChild } = _a, hiddenProps = __rest(_a, ["children"]);
            const key = child.key || index;
            return (_createElement(Hidden, Object.assign({}, hiddenProps, { key: key }), className => renderItem({ className, child: hiddenChild, index: renderedIndex })));
        }
        return renderItem({ child, index: renderedIndex });
    });
    // Classnames and attributes are written here so we can assign nowrap to the root element based on the children
    const rootClassNames = classNames(s.root, className, gap ? s['--gap'] : null, width != null && s['--width'], textAlign && s[`--align-text-${textAlign}`], backgroundColor && s[`--bg-${backgroundColor}`], borderColor && s[`--bd-${borderColor}`], borderColor && s['--bd'], shadow && s[`--shadow-${shadow}`], borderRadius && s[`--br-${borderRadius}`], overflow && s[`--overflow-${overflow}`], 
    // animated && s['--animated'],
    (isFlex || nowrap) && s['--flex'], ...responsiveClassNames(s, '--direction', direction), ...responsiveClassNames(s, '--align', align), ...responsiveClassNames(s, '--justify', justify), 
    // Wrap and nowrap are separate here because inverting any of them could result into a false value which will be ignored by classNames
    ...responsiveClassNames(s, '--nowrap', nowrap || wrap === false), ...responsiveClassNames(s, '--wrap', wrap), ...responsiveClassNames(s, '--bleed', typeof bleed === 'number'
        ? true
        : bleed &&
            responsivePropDependency(bleed, value => typeof value === 'number' && value !== 0)));
    const rootVariables = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, attributes === null || attributes === void 0 ? void 0 : attributes.style), responsiveVariables('--st-_p-t', padding &&
        responsivePropDependency(padding, value => (typeof value === 'number' ? value : value[0])))), responsiveVariables('--st-_p-r', padding &&
        responsivePropDependency(padding, value => (typeof value === 'number' ? value : value[1])))), responsiveVariables('--st-_p-b', padding &&
        responsivePropDependency(padding, value => { var _a; return typeof value === 'number' ? value : (_a = value[2]) !== null && _a !== void 0 ? _a : value[0]; }))), responsiveVariables('--st-_p-l', padding &&
        responsivePropDependency(padding, value => { var _a; return typeof value === 'number' ? value : (_a = value[3]) !== null && _a !== void 0 ? _a : value[1]; }))), responsiveVariables('--st-_g', gap)), responsiveVariables('--st-_w', width)), responsiveVariables('--st-_h', height)), responsiveVariables('--st-_mh', maxHeight)), responsiveVariables('--st-_mw', maxWidth)), responsiveVariables('--st-_b', bleed));
    return (
    // @ts-expect-error TS throws an error due to the type complexity It still resolves the attributes correctly based on the tag
    _jsx(TagName, Object.assign({ "data-testid": "st-view" }, attributes, { className: rootClassNames, style: rootVariables, children: formattedChildren })));
};
View.Item = ViewItem;
